package com.trevorwiebe.timesheet.loads.presentation.admin

import androidx.compose.foundation.gestures.detectDragGesturesAfterLongPress
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.width
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInRoot
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalViewConfiguration
import androidx.compose.ui.platform.ViewConfiguration
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.calendar.domain.vibrate
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import kotlinx.datetime.LocalDate

// Cross-column drag-and-drop for the Loads board, mirroring the iOS implementation: any load card
// long-press-lifts into a floating ghost, drop zones (each driver section on each date board, plus
// the Unscheduled list) register their window bounds, and releasing over a zone dispatches one
// OnMoveLoad carrying that zone's date + employee. All coordinates are in window-root space so
// zones on different pager pages / scrolled columns hit-test correctly.

private const val DRAG_LIFT_DELAY_MS = 300L

/**
 * Shortens the long-press delay before a card lifts into a drag, leaving every other gesture
 * setting (touch slop, double-tap timing, ...) at the platform default. Wraps the whole board so
 * both the dispatch zones and the Unscheduled list get the same feel.
 */
@Composable
fun BoardDragLiftDelay(content: @Composable () -> Unit) {
    val base = LocalViewConfiguration.current
    val config = remember(base) {
        object : ViewConfiguration by base {
            override val longPressTimeoutMillis: Long = DRAG_LIFT_DELAY_MS
        }
    }
    CompositionLocalProvider(LocalViewConfiguration provides config, content = content)
}

data class LoadDropZone(
    val bounds: Rect,
    val date: LocalDate?,
    val employeeId: String,
    val carrierId: String = "",
)

/** Shared state for one in-flight load drag; hoisted to the screen so every column sees it. */
class LoadDragState {
    var draggedLoad by mutableStateOf<Load?>(null)
        private set

    /** Current finger position in window-root coordinates. */
    var pointer by mutableStateOf(Offset.Zero)
        private set

    /** Where inside the card the finger grabbed it, so the ghost stays under the finger. */
    var grabOffset = Offset.Zero
        private set

    /** The lifted card's size, so the ghost matches it. */
    var cardSize by mutableStateOf(IntSize.Zero)
        private set

    val zones = mutableStateMapOf<String, LoadDropZone>()

    var hoveredZoneKey by mutableStateOf<String?>(null)
        private set

    val isDragging: Boolean get() = draggedLoad != null

    fun start(load: Load, pointerInRoot: Offset, grab: Offset, size: IntSize) {
        draggedLoad = load
        pointer = pointerInRoot
        grabOffset = grab
        cardSize = size
        updateHover()
    }

    fun move(pointerInRoot: Offset) {
        pointer = pointerInRoot
        updateHover()
    }

    /** Ends the drag and returns the zone under the finger, if any. */
    fun end(): LoadDropZone? {
        val zone = hoveredZoneKey?.let { zones[it] }
        cancel()
        return zone
    }

    fun cancel() {
        draggedLoad = null
        hoveredZoneKey = null
    }

    private fun updateHover() {
        hoveredZoneKey = zones.entries.firstOrNull { it.value.bounds.contains(pointer) }?.key
    }
}

/**
 * Makes a load card a drag source: long-press lifts it, and releasing over a zone calls [onDropped]
 * with that zone. The gesture is keyed on the load id so mid-drag state refreshes don't cancel it.
 */
@Composable
fun Modifier.loadDragSource(
    dragState: LoadDragState,
    load: Load,
    onDropped: (Load, LoadDropZone) -> Unit,
): Modifier {
    // A live handle to this node's coordinates: localToRoot stays correct even as the pager
    // flips or columns pan mid-drag.
    val coords = remember { mutableStateOf<LayoutCoordinates?>(null) }
    val currentLoad by rememberUpdatedState(load)
    val currentOnDropped by rememberUpdatedState(onDropped)
    return this
        .onGloballyPositioned { coords.value = it }
        .pointerInput(dragState, load.id) {
            detectDragGesturesAfterLongPress(
                onDragStart = { offset ->
                    val c = coords.value?.takeIf { it.isAttached } ?: return@detectDragGesturesAfterLongPress
                    vibrate()
                    dragState.start(currentLoad, c.localToRoot(offset), offset, c.size)
                },
                onDrag = { change, _ ->
                    change.consume()
                    coords.value?.takeIf { it.isAttached }?.let {
                        dragState.move(it.localToRoot(change.position))
                    }
                },
                onDragEnd = {
                    dragState.end()?.let { currentOnDropped(currentLoad, it) }
                },
                onDragCancel = { dragState.cancel() },
            )
        }
}

/** Registers this node as the drop zone [key]; bounds refresh on every layout/scroll change. */
@Composable
fun Modifier.loadDropTarget(
    dragState: LoadDragState,
    key: String,
    date: LocalDate?,
    employeeId: String,
    carrierId: String = "",
): Modifier {
    DisposableEffect(dragState, key) {
        onDispose { dragState.zones.remove(key) }
    }
    return onGloballyPositioned {
        dragState.zones[key] = LoadDropZone(it.boundsInRoot(), date, employeeId, carrierId)
    }
}

/** The floating copy of the dragged card, drawn over the whole board and pinned to the finger. */
@Composable
fun BoxScope.LoadDragGhost(dragState: LoadDragState, locations: List<Location>) {
    val load = dragState.draggedLoad ?: return
    var overlayOrigin by remember { mutableStateOf(Offset.Zero) }
    val ghostWidth = with(LocalDensity.current) { dragState.cardSize.width.toDp() }
    Box(
        Modifier
            .matchParentSize()
            .onGloballyPositioned { overlayOrigin = it.positionInRoot() }
    ) {
        LoadCard(
            load = load,
            locations = locations,
            onClick = {},
            isDragging = true,
            showBottomDivider = false,
            modifier = Modifier
                .offset {
                    val topLeft = dragState.pointer - dragState.grabOffset - overlayOrigin
                    IntOffset(topLeft.x.toInt(), topLeft.y.toInt())
                }
                .width(ghostWidth)
                .shadow(8.dp),
        )
    }
}

/** -1 / +1 when [pointer] sits inside the left/right edge strip of [bounds], else 0. */
fun horizontalEdgeDirection(pointer: Offset, bounds: Rect, edgeWidthPx: Float): Int = when {
    pointer.x < bounds.left + edgeWidthPx -> -1
    pointer.x > bounds.right - edgeWidthPx -> +1
    else -> 0
}
